import React, { useEffect, useState, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sesli bildirim (WebAudio ile bell efekti — mp3 dosyası gerekmez)
function playBell() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const notes = [1200, 1600, 1200, 1600];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = f;
      g.gain.value = 0.0001;
      o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.18;
      g.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
      o.start(t); o.stop(t + 0.17);
    });
  } catch (e) { /* noop */ }
}

export function SnackBarTv() {
  const [media, setMedia] = useState([]);
  const [mediaIdx, setMediaIdx] = useState(0);
  const [ready, setReady] = useState([]);   // hazır olanlar (numaraları alt strip)
  const [highlight, setHighlight] = useState(null); // ortada yanıp sönen sipariş no
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const seenReadyRef = useRef(new Set());   // yeni hazırlar için pop-up tetikleyici

  // Medya listesi ve hazır siparişler polling
  const loadMedia = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/snack-bar/public/tv-media`);
      const j = await r.json();
      setMedia(j.items || []);
    } catch { /* */ }
  }, []);

  const loadReady = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/snack-bar/display`);
      const j = await r.json();
      const items = j.items || [];
      // Yeni bir hazır sipariş tespit et → ortada patlat
      const newOnes = items.filter(o => !seenReadyRef.current.has(o.order_no));
      if (newOnes.length && audioUnlocked) {
        const latest = newOnes[newOnes.length - 1];
        setHighlight(latest);
        playBell();
        setTimeout(() => setHighlight(null), 8000); // 8 saniye ortada
      }
      newOnes.forEach(o => seenReadyRef.current.add(o.order_no));
      setReady(items);
    } catch { /* */ }
  }, [audioUnlocked]);

  useEffect(() => { loadMedia(); const i = setInterval(loadMedia, 30000); return () => clearInterval(i); }, [loadMedia]);
  useEffect(() => { loadReady(); const i = setInterval(loadReady, 2500); return () => clearInterval(i); }, [loadReady]);

  // Slideshow: kısa süreli görsel/video döngüsü
  useEffect(() => {
    if (!media.length) return;
    const cur = media[mediaIdx % media.length];
    if (cur?.kind === 'video') return; // video kendi bittikten sonra ilerlerken atlar (onEnded)
    const dur = (cur?.duration_sec || 6) * 1000;
    const t = setTimeout(() => setMediaIdx(i => (i + 1) % media.length), dur);
    return () => clearTimeout(t);
  }, [media, mediaIdx]);

  const cur = media.length ? media[mediaIdx % media.length] : null;
  const mediaSrc = cur ? (cur.url.startsWith('http') ? cur.url : `${API_URL}${cur.url}`) : '';

  // Ses kilidini aç butonu (tarayıcı ilk kullanıcı etkileşimine kadar ses çalmıyor)
  if (!audioUnlocked) {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center text-white" data-testid="tv-unlock">
        <button
          onClick={() => { playBell(); setAudioUnlocked(true); }}
          className="px-10 py-6 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-2xl font-bold shadow-2xl hover:scale-105 transition-transform"
          data-testid="tv-unlock-btn"
        >
          🔊 EKRANI BAŞLAT
          <div className="text-sm font-normal opacity-80 mt-2">(Sesli bildirim için tıklayın)</div>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-950 overflow-hidden font-sans" data-testid="tv-root">
      {/* Slideshow media (fullscreen background) */}
      {cur ? (
        cur.kind === 'video' ? (
          <video
            key={cur.id}
            src={mediaSrc}
            autoPlay muted playsInline
            onEnded={() => setMediaIdx(i => (i + 1) % media.length)}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <img
            key={cur.id}
            src={mediaSrc}
            alt={cur.title || ''}
            className="absolute inset-0 w-full h-full object-cover animate-[fadeIn_0.8s_ease-in-out]"
          />
        )
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center">
          <div className="text-center text-slate-400">
            <div className="text-5xl font-black tracking-wide text-emerald-400 mb-3">GRAND KAPTAN</div>
            <div className="text-lg">Snack Bar — Tanıtım medyası yüklenmedi</div>
          </div>
        </div>
      )}

      {/* Dim overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70 pointer-events-none" />

      {/* Header: brand */}
      <div className="absolute top-6 left-8 flex items-center gap-3 text-white z-10">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-2xl">
          <span className="text-2xl">🍽</span>
        </div>
        <div>
          <div className="text-3xl font-black tracking-tight">GRAND KAPTAN</div>
          <div className="text-sm uppercase tracking-widest text-emerald-300">Snack Bar</div>
        </div>
      </div>

      {/* Header sağ: saat */}
      <div className="absolute top-6 right-8 text-right text-white z-10">
        <Clock />
      </div>

      {/* Media title / caption (varsa) */}
      {cur?.title && (
        <div className="absolute bottom-40 left-8 right-8 text-white z-10">
          <div className="text-4xl font-bold tracking-tight drop-shadow-lg">{cur.title}</div>
        </div>
      )}

      {/* Alt strip: HAZIR sipariş numaraları */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/95 to-slate-900/70 backdrop-blur-md border-t border-emerald-500/40 z-10">
        <div className="p-4">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-300 mb-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> HAZIR SİPARİŞLER
          </div>
          {ready.length === 0 ? (
            <div className="text-slate-500 text-sm">Şu an hazır sipariş yok — afiyet olsun</div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {ready.slice(0, 12).map(o => (
                <div key={o.order_no} className="bg-emerald-500/20 border-2 border-emerald-400/60 rounded-xl px-5 py-2 min-w-[110px] text-center backdrop-blur-sm">
                  <div className="text-[10px] text-emerald-200 uppercase tracking-widest">No</div>
                  <div className="text-3xl font-black text-white font-mono">{o.order_no}</div>
                  <div className="text-[10px] text-emerald-200 truncate">{o.table_name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Highlight: yeni hazır sipariş — ortada 8sn yanıp söner */}
      {highlight && (
        <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none" data-testid="tv-highlight">
          <div className="bg-black/70 backdrop-blur-2xl absolute inset-0" />
          <div className="relative bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl px-16 py-12 shadow-[0_0_120px_20px_rgba(52,211,153,0.6)] border-4 border-white/20 animate-[pulse_1.2s_ease-in-out_infinite]">
            <div className="text-center text-white">
              <div className="text-2xl uppercase tracking-widest mb-2">Siparişiniz Hazır</div>
              <div className="text-[160px] font-black leading-none font-mono drop-shadow-2xl">{highlight.order_no}</div>
              <div className="text-3xl font-bold mt-4">{highlight.table_name}</div>
              <div className="text-sm uppercase tracking-widest mt-3 opacity-80">Lütfen bara/mutfağa gelin</div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(1.03); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(i); }, []);
  return (
    <div>
      <div className="text-4xl font-black font-mono tabular-nums drop-shadow">{now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
      <div className="text-sm text-emerald-300 uppercase tracking-widest">{now.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
    </div>
  );
}
